import React, { useCallback, useEffect, useState } from "react";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { Grid } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";

import { getContainerSKURecords } from "api/purchase";
import {
  Button,
  DatePickerGroup,
  InputContainer,
  TextInput,
} from "components/Form";
import PageTitle from "components/PageTitle";
import DefaultTable from "components/Table";

import { ExportIcon, QrCode2, Receipt } from "icons";

import { initialAPIListValue } from "utils/constants";
import { convertMergedFiltersToQueryString } from "utils/filter";
import getClearAdornment from "utils/form";

import useStyles from "../containers/styles";
import { exportToExcel } from "utils/exportToExcel";
import { convertDataToTableView } from "utils/convert";
import { timezoneFormat } from "utils/date";

const exportHeaders = [
  { id: "container_number" },
  { id: "sku" },
  { id: "qty" },
  {
    id: "date",
    label: "Date",
    render: (r) => timezoneFormat(r.ata || r.created_at, true),
  },
];

export default function SellerContainerSKUList() {
  const [t] = useTranslation();
  const classes = useStyles();
  const theme = useTheme();

  const initialCells = [
    {
      id: "container_number",
      label: "Container",
      render: (r) => r.container_number,
      sortable: true,
      flex: 1,
    },
    {
      id: "sku",
      label: "SKU",
      render: (r) => r.sku,
      sortable: true,
      flex: 1,
    },
    {
      id: "qty",
      label: "QTY",
      render: (r) => r.qty,
      sortable: true,
      flex: 1,
    },
    {
      id: "date",
      label: "Date",
      render: (r) => timezoneFormat(r.ata || r.created_at, true),
      sortable: true,
      flex: 1,
    },
  ];

  const [cells] = useState(initialCells);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [search, setSearch] = useState({
    po_number: "",
    container_number: "",
    bol_number: "",
    seller_id: 2,
    sku: "",
  });

  const [selectedFilters, setSelectedFilters] = useState({
    vendor: [],
    po_type: [],
    customer: [],
    importer: [],
    ship_to_port: [],
    final_destination: [],
    line: [],
  });
  const [containers, setContainers] = useState(initialAPIListValue);
  const [headerCounts, setHeaderCounts] = useState({});
  const [exportDisabled, setExportDisabled] = useState(false);
  const [orderDirection, setOrderDirection] = useState("desc");
  const [orderBy, setOrderBy] = useState("eta");

  useEffect(() => {
    if (containers.isFetching) {
      let params = {
        ...search,
        limit: rowsPerPage,
        offset: rowsPerPage * page,
      };
      if (orderDirection && orderBy) {
        params.ordering = `${orderDirection === "asc" ? "" : "-"}${orderBy}`;
      }
      getContainerSKURecords({
        params,
        mergedFilters: convertMergedFiltersToQueryString(selectedFilters),
        setFunc: (res) => {
          setContainers(res);
          setHeaderCounts(res.customCounts);
        },
        paginated: true,
      });
    }
  }, [
    search,
    page,
    rowsPerPage,
    selectedFilters,
    containers.isFetching,
    orderDirection,
    orderBy,
  ]);

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
    setContainers((prev) => ({ ...prev, isFetching: true }));
  };

  const handleChangeRowsPerPage = ({ target: { value } }) => {
    setRowsPerPage(parseInt(value, 10));
    debouncedSearchHandler();
  };

  const onSort = (header) => {
    const isAsc = orderBy === header && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(header);
    setContainers((prev) => ({ ...prev, isFetching: true }));
  };

  const handleChangeFilterInput = (prop) => ({ target: { value } }) => {
    setSearch((prev) => ({ ...prev, [prop]: value }));
    debouncedSearchHandler();
  };

  const clearHandler = (input) => {
    setSearch({ ...search, [input]: "" });
    debouncedSearchHandler();
  };

  const clearAdornment = (input) =>
    getClearAdornment(input, clearHandler, search);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchHandler = useCallback(
    debounce(() => {
      setPage(0);
      setContainers({ ...containers, isFetching: true });
    }, 500),
    [],
  );

  const exportData = (data) => {
    const today = new Date();
    exportToExcel(
      [],
      `Containers-${today.toLocaleDateString()}`,
      true,
      convertDataToTableView(data, exportHeaders),
      setExportDisabled,
    );
  };

  const handleExportData = () => {
    getContainerSKURecords({
      params: {
        limit: containers.count,
        ...search,
      },
      mergedFilters: convertMergedFiltersToQueryString(selectedFilters),
      setFunc: ({ items: { results } }) => exportData(results),
    });
  };

  return (
    <>
      <PageTitle
        title="Container SKU List"
        button={
          <>
            <Button
              endIcon={<ExportIcon color="white" />}
              variant="contained"
              color="primary-light"
              size="medium"
              onClick={() => handleExportData()}
            >
              Export
            </Button>
          </>
        }
      />
      <InputContainer>
        <Grid container>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <TextInput
              id="container_number"
              label={t("container_number")}
              variant="outlined"
              fullWidth
              value={search.container_number}
              onChange={handleChangeFilterInput("container_number")}
              InputProps={clearAdornment("container_number")}
              LeftIcon={Receipt}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <TextInput
              id="sku"
              label={t("SKU")}
              variant="outlined"
              fullWidth
              value={search.sku}
              onChange={handleChangeFilterInput("sku")}
              InputProps={clearAdornment("sku")}
              LeftIcon={QrCode2}
            />
          </Grid>
          <DatePickerGroup
            startDateValue={search.start_date}
            startDateOnChange={handleChangeFilterInput("start_date")}
            endDateValue={search.end_date}
            endDateOnChange={handleChangeFilterInput("end_date")}
            itemGridProps={{ xs: 12, sm: 6, md: 4, lg: 2, xl: 2 }}
          />
        </Grid>
      </InputContainer>
      <Grid container spacing={4}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <DefaultTable
            data={containers}
            headers={cells}
            headerCounts={headerCounts}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            filters={search}
            onFilter={() => {
              setContainers({ ...containers, isFetching: true });
            }}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            highlightColor={theme.palette.primary.main}
            className={classes.table}
            highlightStyle={(_r) => ({
              backgroundColor: "white",
              borderBottom: "1px solid #dddddd90",
              height: "4rem",
            })}
            disableExportData={true}
            exportDisabled={exportDisabled}
            handleExportData={handleExportData}
            sortActions={{ onSort, orderBy, orderDirection }}
          />
        </Grid>
      </Grid>
    </>
  );
}
